import React, { useEffect, useRef } from 'react';
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useUnavailableDates } from '../../hooks/useUnavailableDates';
import { addOneDay, formatBackendDateForDisplay } from '../../services/dateUtils';

const UNAVAILABLE_COLOR = 'rgba(255, 0, 0, 0.25)';
const BOOKING_OVERLAY_COLOR = 'rgba(255, 0, 0, 0.12)';

const buildUnavailableEvent = (ud) => ({
  id: `unavailable-${ud.id}`,
  start: ud.start_date,
  end: addOneDay(ud.end_date),
  display: 'background',
  backgroundColor: UNAVAILABLE_COLOR,
  classNames: ['unavailable-date'],
});

const buildBookingEvent = (bd, i) => ({
  id: `booking-${i}`,
  start: bd.start_date,
  end: addOneDay(bd.end_date),
  display: 'background',
  backgroundColor: BOOKING_OVERLAY_COLOR,
  classNames: ['unavailable-date', 'unavailable-date--booking'],
});

const formatDateRange = (start, end) => {
  const startLabel = formatBackendDateForDisplay(start);
  if (start === end) {
    return <strong>{startLabel}</strong>;
  }
  return (
    <>
      <strong>{startLabel}</strong> to <strong>{formatBackendDateForDisplay(end)}</strong>
    </>
  );
};

const UserAvailabilityCalendar = ({ userId, canEdit = false, bookingDates = [] }) => {
  const calendarRef = useRef(null);
  const calendarInstanceRef = useRef(null);
  const unavailableDatesRef = useRef([]);
  const bookingDatesRef = useRef([]);
  const {
    unavailableDates,
    isLoading,
    error,
    addUnavailableRange,
    removeUnavailableRange,
  } = useUnavailableDates(userId);

  const [pendingAdd, setPendingAdd] = React.useState(null); // { startDate, endDate }
  const [pendingRemove, setPendingRemove] = React.useState(null); // unavailableId
  const [unavailableError, setUnavailableError] = React.useState(null);

  unavailableDatesRef.current = unavailableDates;
  bookingDatesRef.current = bookingDates;

  // Build the calendar once; events are synced in place afterwards so edits
  // don't tear down and re-render the whole calendar (which caused flicker).
  useEffect(() => {
    if (!calendarRef.current) return;

    const calendar = new Calendar(calendarRef.current, {
      plugins: [dayGridPlugin, interactionPlugin],
      initialView: 'dayGridMonth',
      events: [],
      selectable: canEdit,
      selectMirror: true,
      unselectAuto: true,
      select: function (info) {
        // end is exclusive in FullCalendar — convert back to inclusive
        const endDate = new Date(info.endStr);
        endDate.setDate(endDate.getDate() - 1);
        const inclusiveEnd = endDate.toISOString().slice(0, 10);
        const startDate = info.startStr;
        setUnavailableError(null);

        // Background events never fire eventClick, so removal is detected here:
        // a click/drag contained within an existing manual range removes it.
        const existingRange = unavailableDatesRef.current.find(
          ud => startDate >= ud.start_date && inclusiveEnd <= ud.end_date
        );
        if (existingRange) {
          setPendingRemove(existingRange.id);
          calendar.unselect();
          return;
        }

        // Booking overlays are read-only; ignore selections contained within them.
        const bookingRange = bookingDatesRef.current.find(
          bd => startDate >= bd.start_date && inclusiveEnd <= bd.end_date
        );
        if (bookingRange) {
          calendar.unselect();
          return;
        }

        setPendingAdd({ startDate, endDate: inclusiveEnd });
        calendar.unselect();
      },
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: '',
      },
      height: 500,
      editable: false,
    });

    calendar.render();
    calendarInstanceRef.current = calendar;

    return () => {
      if (calendarInstanceRef.current) {
        calendarInstanceRef.current.destroy();
        calendarInstanceRef.current = null;
      }
    };
  }, [canEdit]);

  // Sync background events whenever the underlying ranges change.
  useEffect(() => {
    const calendar = calendarInstanceRef.current;
    if (!calendar) return;

    calendar.getEvents().forEach(event => {
      if (event.id && (event.id.startsWith('unavailable-') || event.id.startsWith('booking-'))) {
        event.remove();
      }
    });

    unavailableDates.forEach(ud => calendar.addEvent(buildUnavailableEvent(ud)));
    bookingDates.forEach((bd, i) => calendar.addEvent(buildBookingEvent(bd, i)));
  }, [unavailableDates, bookingDates, canEdit]);

  const confirmAddUnavailable = async () => {
    if (!pendingAdd) return;
    try {
      await addUnavailableRange(pendingAdd.startDate, pendingAdd.endDate);
    } catch (err) {
      setUnavailableError(err.message || 'Failed to mark dates as unavailable.');
    }
    setPendingAdd(null);
  };

  const confirmRemoveUnavailable = async () => {
    if (!pendingRemove) return;
    try {
      await removeUnavailableRange(pendingRemove);
    } catch (err) {
      setUnavailableError(err.message || 'Failed to remove unavailability.');
    }
    setPendingRemove(null);
  };

  const rangeToRemove = pendingRemove
    ? unavailableDates.find(ud => ud.id === pendingRemove)
    : null;

  return (
    <div className="user-availability-calendar">
      {error && <p className="unavailability-calendar-error">{error}</p>}
      {isLoading && <p className="unavailability-calendar-loading">Loading availability…</p>}
      <div ref={calendarRef} />
      <p className="unavailability-calendar-legend">
        <span className="unavailability-calendar-legend__swatch" />
        Unavailable dates
      </p>
      {canEdit && (
        <p className="unavailability-calendar-hint">
          To mark dates as unavailable, click and drag across the days you want to block. Click an existing unavailable period to remove it.
        </p>
      )}
      {unavailableError && (
        <div className="unavailable-error" style={{ color: '#d63638', marginTop: '8px' }}>
          {unavailableError}
        </div>
      )}
      {pendingAdd && (
        <div className="unavailable-confirm-modal">
          <div className="unavailable-confirm-modal__overlay" onClick={() => setPendingAdd(null)} />
          <div className="unavailable-confirm-modal__box">
            <h3>Mark as Unavailable?</h3>
            <p>Mark {formatDateRange(pendingAdd.startDate, pendingAdd.endDate)} as unavailable?</p>
            <div className="unavailable-confirm-modal__actions">
              <button className="button button-primary" onClick={confirmAddUnavailable}>Confirm</button>
              <button className="button" onClick={() => setPendingAdd(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {pendingRemove && (
        <div className="unavailable-confirm-modal">
          <div className="unavailable-confirm-modal__overlay" onClick={() => setPendingRemove(null)} />
          <div className="unavailable-confirm-modal__box">
            <h3>Remove Unavailability?</h3>
            <p>
              {rangeToRemove
                ? <>Mark {formatDateRange(rangeToRemove.start_date, rangeToRemove.end_date)} as available again?</>
                : 'Mark these dates as available again?'}
            </p>
            <div className="unavailable-confirm-modal__actions">
              <button className="button button-primary" onClick={confirmRemoveUnavailable}>Yes, Remove</button>
              <button className="button" onClick={() => setPendingRemove(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAvailabilityCalendar;
